import React, { createContext, ReactNode, useCallback, useEffect, useReducer } from 'react';
import { Howl, Howler } from 'howler';
import { soundReducer, getDefaultAppSoundState } from './reducer';
import {
  ActivityAudioAction,
  LoadedSounds,
  SoundType,
  SoundMap,
  UseActivityAudioState,
  UseActivityAudioDispatch,
} from './types';
import { getAudioAsset, getAudioAssets, getRandomSound, isEmptyAudio, removeSound } from './util';
import { formatTimeStamp, mockSoundMap } from '../../util/activity-audio-util';

export type UseAppSound = [UseActivityAudioState, UseActivityAudioDispatch];

export const ActivityAudioContext = createContext<any>([]);

export const ActivityAudioProvider: React.FC<{
  children?: ReactNode | undefined;
}> = ({ children }) => {
  // TODO: we should also get this from user settings / accessibility
  let DISPATCH_INTERVAL;

  // TODO: load user here to determine any optional sound settings
  const [state, dispatch] = useReducer<React.Reducer<UseActivityAudioState, ActivityAudioAction>>(
    soundReducer,
    getDefaultAppSoundState()
  );

  const _displayCaption = (audioTimeStamp: number, { cues }: any = {}): void => {
    const timeStamp = parseFloat(audioTimeStamp.toFixed(1));
    const cue = cues.find(({ start }) => formatTimeStamp(start) === timeStamp);

    if (cue) {
      const { end, payload, start } = cue;
      dispatch({ type: 'SET_CAPTION', payload: { caption: payload } });

      clearInterval(DISPATCH_INTERVAL);

      DISPATCH_INTERVAL = setTimeout(() => {
        dispatch({ type: 'SET_CAPTION', payload: { caption: '' } });
      }, (formatTimeStamp(end) - formatTimeStamp(start)) * 1500);
    }
  };

  const _setAudioAsset = async (
    { src, name }: SoundType,
    soundMap: SoundMap | null,
    options?: any
  ): Promise<void> => {
    const asset = src || getRandomSound(name, soundMap);
    if (asset.src) {
      const [sound, captions] = await getAudioAsset(asset, options);
      dispatch({ type: 'SET_SOUND', payload: { name, sound, captions } });
    }
  };

  const _setCommonSounds = async (): Promise<void> => {
    _setLoading(true);

    const genericSoundMap: SoundMap = await new Promise((resolve) =>
      setTimeout(() => resolve(mockSoundMap), 500)
    );

    dispatch({ type: 'SET_GENERIC_SOUND_LIST', payload: { genericSoundMap } });

    _setLoading(false);
  };

  const _setLoading = (loading: boolean): void =>
    dispatch({ type: 'SET_LOADING', payload: { loading } });

  const _setPlaySound = useCallback(
    async (asset: Howl, refreshCallback?: () => void, showCCs = true): Promise<Howl> =>
      new Promise((resolve) => {
        const { sound: currentSound, captions } = asset;

        currentSound?.once('end', () => {
          stopSound();

          if (refreshCallback) {
            refreshCallback();
          }

          resolve({ success: true });
        });

        currentSound?.once('playerror', (_: any, error: any) => {
          dispatch({ type: 'STOP_SOUND' });

          resolve({ success: false, error });
        });

        dispatch({
          type: 'PLAY_SOUND',
          payload: {
            sound: currentSound,
            isPlaying: true,
          },
        });

        currentSound?.once('play', () => {
          if (showCCs) {
            const getTimeStamp = () => {
              if (currentSound.playing()) {
                const timeStamp = currentSound.seek();
                _displayCaption(timeStamp, captions);
                setTimeout(getTimeStamp, 100);
              }
            };

            getTimeStamp();
          }
        });

        currentSound?.play();
      }),
    [dispatch, state]
  );

  const loadAudioAssets = useCallback(
    async (assets: SoundType[]) => {
      _setLoading(true);
      const { genericSoundMap } = state;
      const downloadList = assets.map((asset) => getAudioAssets(asset, genericSoundMap));
      const results = await Promise.all(downloadList);
      const filteredResults = results.filter(Boolean);
      dispatch({ type: 'SET_SOUNDS', payload: { sounds: filteredResults as LoadedSounds } });
      _setLoading(false);
    },
    [dispatch, state.genericSoundMap]
  );

  const playMultipleSounds = useCallback(
    (delay = 0, ...sounds): Promise<string> => {
      return new Promise((resolve) => {
        const soundsArray: Howl[] = [];
        const filteredSounds = sounds.filter((sound) => !!sound);
        let currentSound: Howl = null;

        const pushToSoundsArray = (sound: any): void => {
          if (sound && 'once' in sound) {
            soundsArray.push(sound);
          }
        };

        filteredSounds.forEach((filteredSound) => {
          if (Array.isArray(filteredSound)) {
            filteredSound.forEach((sound) => {
              const { sound: stateSound } = state?.sounds?.[sound] ?? {};
              pushToSoundsArray(stateSound);
            });
          } else {
            const { sound } = state?.sounds?.[filteredSound] ?? {};
            pushToSoundsArray(sound);
          }
        });

        if (!soundsArray.length) {
          console.error('No sounds to play');
          resolve('');
        }

        soundsArray.forEach((sound, index) => {
          sound.once(
            'end',
            index < soundsArray.length - 1
              ? () => {
                  currentSound = soundsArray[index + 1];
                  _setPlaySound({ sound: currentSound, captions: { cues: [] } });
                }
              : () => resolve('')
          );
        });

        setTimeout(() => {
          currentSound = soundsArray[0];
          _setPlaySound({ sound: currentSound, captions: { cues: [] } });
        }, delay);
      });
    },
    [state.sounds]
  );

  const playSound = useCallback(
    (soundType: SoundType, refreshSound?: boolean): { emptyAudio: boolean } | Promise<Howl> =>
      new Promise((resolve) => {
        const { genericSoundMap, sounds } = state;
        const asset = sounds[soundType.name];
        const sound = asset?.sound;

        const callback = refreshSound
          ? useCallback(() => {
              removeAudioAsset(soundType.name, sound);
              _setAudioAsset(soundType, genericSoundMap);
            }, [soundType])
          : undefined;

        resolve(
          !asset || isEmptyAudio(asset) ? { emptyAudio: true } : _setPlaySound(asset, callback)
        );
      }),

    [dispatch, state.genericSoundMap, state.sounds]
  );

  const playSoundFromState = useCallback(
    (asset: Howl, interrupt?: boolean): { emptyAudio: boolean } | Promise<Howl> =>
      new Promise((resolve) => {
        interrupt && stopSound();
        resolve(!asset || isEmptyAudio(asset) ? { emptyAudio: true } : _setPlaySound(asset));
      }),
    [dispatch, state.currentSound]
  );

  const removeAudioAsset = (name: string, sound: any): void => {
    removeSound(sound);

    dispatch({ type: 'REMOVE_SOUND', payload: { name } });
  };

  const setMute = useCallback(
    (mute: boolean) => {
      Howler.mute(mute);

      dispatch({ type: 'SET_MUTE', payload: { mute } });
    },
    [dispatch]
  );

  const setVolume = useCallback(
    (volume: number) => {
      Howler.volume(volume);

      dispatch({ type: 'SET_VOLUME', payload: { volume } });
    },
    [dispatch]
  );

  const stopSound = useCallback((): void => {
    const { currentSound } = state;
    if (currentSound) currentSound.stop();

    dispatch({ type: 'STOP_SOUND' });
  }, [dispatch, state.currentSound]);

  useEffect(() => {
    const { genericSoundMap } = state;
    if (!genericSoundMap) _setCommonSounds();
  }, [state.genericSoundMap]);

  return (
    <ActivityAudioContext.Provider
      value={[
        state,
        {
          loadAudioAssets,
          playMultipleSounds,
          playSound,
          playSoundFromState,
          setMute,
          stopSound,
          setVolume,
        },
      ]}
    >
      {children}
    </ActivityAudioContext.Provider>
  );
};

export default ActivityAudioProvider;
